import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';

import { AdminTitle, AdminTrash } from 'components/admin/lib/admin_layout';
import AttributeDef from 'models/configuration/attribute_def';
import Button, { ButtonTypes } from 'components/common/button';
import Checkbox from 'components/common/checkbox';
import connect from 'components/lib/connect';
import CustomerLookupConfig from 'models/customer_lookup_config';
import Err from 'models/err';
import { getErrorMessageList } from 'components/lib/error_handler';
import Input from 'components/common/input';
import InputWithResettableErrors from './input_with_resettable_errors';
import Integrations from 'models/location/integrations';
import FullPageSpinner from 'components/common/full_page_spinner';
import NavigateTo from 'actions/current_location/navigate_to';
import SaveLookupApiConfigAction from 'actions/integrations/save_lookup_api_config';
import Toggle from 'components/common/toggle';

function mapStateToProps({ getProvider }) {
  let externalCustomerLookupStatusProvider = getProvider('externalCustomerLookupStatus');
  let lookupApiConfigProvider = getProvider('lookupApiConfig');
  const profileCustomAttributes = getProvider('customerProfileDef');

  return {
    errors: lookupApiConfigProvider.getErrors(),
    hasConnector: _.get(externalCustomerLookupStatusProvider.get(), 'hasConnector'),
    initialCustomerLookupConfig: lookupApiConfigProvider.get(),
    isPending: lookupApiConfigProvider.isPending(),
    profileCustomAttributes: _.get(profileCustomAttributes.get(), 'attributes'),
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onCancel,
    onSave,
  };

  function onSave(newConfig) {
    executeAction(SaveLookupApiConfigAction, newConfig);
  }

  function onCancel() {
    executeAction(NavigateTo, Integrations.create());
  }
}

export const HIDDEN_STRING = '•••••••';

export class LookupApiConfigForm extends React.Component {
  constructor(props) {
    super(props);

    let credentialsState = {
      username: '',
      realm: '',
    };
    let hasPassword,
      hasSigningKey,
      disabled,
      overwritingEnabled = false;

    let headersState = [];

    let initialLookupApiConfig = {};
    let autoLinkFields;

    if (props.initialCustomerLookupConfig) {
      disabled = props.initialCustomerLookupConfig.disabled;
      overwritingEnabled = props.initialCustomerLookupConfig.overwritingEnabled;
      autoLinkFields = props.initialCustomerLookupConfig.autoLinkFields;
      initialLookupApiConfig = props.initialCustomerLookupConfig.adapterConfiguration;
      hasPassword = !!initialLookupApiConfig.hasPassword;
      hasSigningKey = !!initialLookupApiConfig.hasSigningKey;
      if (initialLookupApiConfig.credentials) {
        credentialsState = _.mapValues(credentialsState, function(value, credentialFieldName) {
          return initialLookupApiConfig.credentials[credentialFieldName] || '';
        });
      }
      if (initialLookupApiConfig.headerKeys) {
        headersState = initialLookupApiConfig.headerKeys.map(headerKey => ({ headerKey }));
      }
    }

    this.state = {
      credentials: credentialsState,
      hasPassword,
      enabled: !disabled,
      errors: this.getStateErrorsFromProps(props, headersState),
      headers: headersState,
      name: _.get(initialLookupApiConfig, 'name', ''),
      hasSigningKey,
      url: _.get(initialLookupApiConfig, 'url', ''),
      autoLinkFields: autoLinkFields || [],
      overwritingEnabled,
    };

    _.bindAll(this, [
      'handleChangeName',
      'handleChangeUrl',
      'handleClickAddHeader',
      'handleClickCancel',
      'handleClickSave',
      'handleRequestSignatureKeyChange',
      'toggleEnabled',
      'toggleOverwritingEnabled',
    ]);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState(function(currentState) {
      return {
        errors: this.getStateErrorsFromProps(nextProps, currentState.headers),
      };
    });
  }

  getStateErrorsFromProps(props, currentHeaders) {
    return {
      headers: currentHeaders.map(function(headerKV, index) {
        return {
          key: getErrorMessageList({ errors: props.errors, attr: `adapterConfiguration.headers.${index}.key` }),
          value: getErrorMessageList({ errors: props.errors, attr: `adapterConfiguration.headers.${index}.value` }),
        };
      }),
      url: getErrorMessageList({ errors: props.errors, attr: 'adapterConfiguration.url' }),
      signingKey: getErrorMessageList({ errors: props.errors, attr: 'adapterConfiguration.signingKey' }),
    };
  }

  render() {
    return (
      <div className="lookupApiConfigForm">
        {this.props.isPending && <FullPageSpinner />}

        <div className="lookupApiConfigForm-section">
          {this.renderNameField()}
          {this.renderUrlField()}
          {this.renderEnabledToggle()}
        </div>

        {this.renderCredentials()}
        {this.renderHeaders()}
        {this.renderRequestSignature()}

        <div className="lookupApiConfigForm-options-header">
          <AdminTitle>Options</AdminTitle>
        </div>

        {this.renderOverwritingEnabledToggle()}
        {this.renderAutoLinkFields()}
        {this.renderButtons()}
      </div>
    );
  }

  handleClickCancel() {
    this.props.onCancel();
  }

  handleClickSave() {
    let configToSave = {
      name: this.state.name,
      url: this.state.url,
      credentials: this.state.credentials,
      headers: this.state.headers,
      signingKey: this.state.signingKey,
      disabled: !this.state.enabled,
      autoLinkFields: this.state.autoLinkFields,
      overwritingEnabled: this.state.overwritingEnabled,
    };

    this.setState(function(currentState) {
      return {
        errors: this.getStateErrorsFromProps({ errors: [] }, currentState.headers),
      };
    });

    this.props.onSave(configToSave);
  }

  renderNameField() {
    return (
      <div className="lookupApiConfigForm-name lookupApiConfigForm-field">
        <span className="lookupApiConfigForm-nameLabel lookupApiConfigForm-label">Customer Master Name</span>
        <InputWithResettableErrors
          className="lookupApiConfigForm-name"
          onChange={this.handleChangeName}
          placeholder="Name"
          value={this.state.name}
        />
      </div>
    );
  }

  handleChangeName(evt) {
    this.handleChangeField('name', evt);
  }

  renderUrlField() {
    return (
      <div className="lookupApiConfigForm-url lookupApiConfigForm-field">
        <span className="lookupApiConfigForm-urlLabel lookupApiConfigForm-label">URL</span>
        <InputWithResettableErrors
          className="lookupApiConfigForm-url"
          errors={this.state.errors.url}
          onChange={this.handleChangeUrl}
          placeholder="Url"
          value={this.state.url}
        />
      </div>
    );
  }

  handleChangeUrl(evt) {
    this.handleChangeField('url', evt);
  }

  renderEnabledToggle() {
    return (
      <div className="lookupApiConfigForm-enabled lookupApiConfigForm-field">
        <span className="lookupApiConfigForm-enabledLabel lookupApiConfigForm-label">Enabled</span>
        <Toggle isEnabled={this.state.enabled} onChange={this.toggleEnabled} />
      </div>
    );
  }

  toggleEnabled(e) {
    this.setState(prevState => ({
      enabled: !prevState.enabled,
    }));
  }

  renderOverwritingEnabledToggle() {
    return (
      <div className="lookupApiConfigForm-overwritingEnabled lookupApiConfigForm-section">
        <div className="lookupApiConfigForm-sectionTitle">Overwrite Existing Fields</div>

        <p>
          Overwrite the existing name, address, and editable custom attributes in the Gladly customer profile with data
          from your customer master.
        </p>

        <div className="lookupApiConfigForm-field">
          <span className="lookupApiConfigForm-overwritingEnabledLabel lookupApiConfigForm-label">Enabled</span>
          <Toggle isEnabled={this.state.overwritingEnabled} onChange={this.toggleOverwritingEnabled} />
        </div>
      </div>
    );
  }

  toggleOverwritingEnabled(e) {
    this.setState(prevState => ({
      overwritingEnabled: !prevState.overwritingEnabled,
    }));
  }

  renderLookupApiConfigFormField(fieldName, fieldValue, fieldChangeHandler, placeholder, isPassword) {
    return (
      <div className={`lookupApiConfigForm-${fieldName} lookupApiConfigForm-field`}>
        <span className={`lookupApiConfigForm-${fieldName}Label lookupApiConfigForm-label`}>
          {_.upperFirst(fieldName)}
        </span>
        <Input
          onChange={fieldChangeHandler}
          value={fieldValue}
          placeholder={placeholder ? placeholder : fieldName.toLowerCase()}
          wrapperClassName={`lookupApiConfigForm-${fieldName}-wrapper`}
          className={`lookupApiConfigForm-${fieldName}-input editableField`}
          type={isPassword ? 'password' : ''}
        />
      </div>
    );
  }

  renderCredentials() {
    return (
      <div className="lookupApiConfigForm-credentials lookupApiConfigForm-section">
        <div className="lookupApiConfigForm-sectionTitle">Basic Auth</div>

        {this.renderLookupApiConfigFormField('username', this.state.credentials.username, evt =>
          this.handleChangeCredentialField('username', evt)
        )}

        {this.renderLookupApiConfigFormField(
          'password',
          this.state.credentials.password,
          evt => this.handleChangeCredentialField('password', evt),
          this.state.hasPassword ? HIDDEN_STRING : 'password',
          true
        )}

        {this.renderLookupApiConfigFormField('realm', this.state.credentials.realm, evt =>
          this.handleChangeCredentialField('realm', evt)
        )}
      </div>
    );
  }

  handleChangeCredentialField(credentialFieldName, evt) {
    let newFieldValue = evt.target.value;
    this.setState(function(currentState) {
      return { credentials: { ...currentState.credentials, [credentialFieldName]: newFieldValue } };
    });
  }

  handleChangeField(fieldName, evt) {
    this.setState({ [fieldName]: evt.target.value });
  }

  renderHeaders() {
    let currentHeaders = _.map(this.state.headers, (keyValueTuple, index) => {
      if (keyValueTuple.headerValue === null) {
        return null;
      }
      return (
        <div key={index} className="lookupApiConfigForm-headers-row lookupApiConfigForm-field">
          <span className="lookupApiConfigForm-headerKey">
            <InputWithResettableErrors
              onChange={evt => this.handleChangeHeaderField(index, 'headerKey', evt)}
              errors={this.state.errors.headers[index] && this.state.errors.headers[index].key}
              value={keyValueTuple.headerKey}
              placeholder="key"
              className="lookupApiConfigForm-headerKey"
            />
          </span>
          <span className="lookupApiConfigForm-headerValue-wrapper">
            <InputWithResettableErrors
              onChange={evt => this.handleChangeHeaderField(index, 'headerValue', evt)}
              errors={this.state.errors.headers[index] && this.state.errors.headers[index].value}
              value={_.get(keyValueTuple.headerValue, '')}
              placeholder={_.isUndefined(keyValueTuple.headerValue) ? HIDDEN_STRING : 'value'}
              className="lookupApiConfigForm-headerValue"
              type="password"
            />
          </span>
          <span className="lookupApiConfigForm-headers-row-controls">
            <AdminTrash onClick={() => this.handleClickDeleteHeader(index)} />
          </span>
        </div>
      );
    });
    return (
      <div className="lookupApiConfigForm-headers lookupApiConfigForm-section">
        <div className="lookupApiConfigForm-sectionTitle">Http Headers</div>

        <div className="lookupApiConfigForm-headers">{currentHeaders}</div>

        <div className="lookupApiConfigForm-headers-controls">
          <Button
            className="lookupApiConfigForm-headers-add"
            onClick={this.handleClickAddHeader}
            buttonType={ButtonTypes.TEXT}
          >
            + http header
          </Button>
        </div>
      </div>
    );
  }

  handleChangeHeaderField(index, fieldName, evt) {
    let newFieldValue = evt.target.value;
    this.setState(function(currentState) {
      let headers = currentState.headers.slice(0);
      headers[index][fieldName] = newFieldValue;
      return { headers };
    });
  }

  handleClickAddHeader() {
    this.setState(function(currentState) {
      let headers = currentState.headers.slice(0);
      headers.push({
        headerKey: '',
        headerValue: '',
      });
      return { headers };
    });
  }

  handleClickDeleteHeader(index) {
    this.setState(function(currentState) {
      let headers = currentState.headers;
      headers[index].headerValue = null;
      return { headers };
    });
  }

  renderRequestSignature() {
    return (
      <div className="lookupApiConfigForm-requestSignature lookupApiConfigForm-section">
        <div className="lookupApiConfigForm-sectionTitle">Request Signature</div>

        <div className="lookupApiConfigForm-requestSignature-signingKey lookupApiConfigForm-field">
          <span className="lookupApiConfigForm-label">Key</span>

          <InputWithResettableErrors
            onChange={this.handleRequestSignatureKeyChange}
            errors={this.state.errors.signingKey}
            value={_.isUndefined(this.state.signingKey) ? '' : this.state.signingKey}
            placeholder={this.state.hasSigningKey ? HIDDEN_STRING : 'signing key'}
            className={`lookupApiConfigForm-signingKey`}
            type="password"
          />
        </div>
      </div>
    );
  }

  renderAutoLinkFields() {
    return (
      <div className="lookupApiConfigForm-autoLink lookupApiConfigForm-section">
        <div className="lookupApiConfigForm-sectionTitle">Fields to auto-link on</div>

        <p>
          When an Agent lands on a Customer Profile in Gladly that isn't already linked to a profile in your customer
          master, Gladly will lookup the Customer using the following fields. If your master responds with a unique
          profile with at least one auto-linking field match and no conflicts, Gladly will automatically link the
          profiles without any user intervention.
        </p>

        <div className="lookupApiConfigForm-autoLink-fields">
          <div>
            <div className="lookupApiConfigForm-field">
              <Checkbox
                key="autoLinkFields-email"
                label="Customer email"
                checked={this.isAutoLinkFieldChecked('emails')}
                size="small"
                value="emails"
                onChange={this.handleChangeAutoLinkFields.bind(this, 'emails')}
              />
            </div>
            <div className="lookupApiConfigForm-subtext">If more than one is available, all will be used in search</div>
          </div>

          <div>
            <div className="lookupApiConfigForm-field">
              <Checkbox
                key="autoLinkFields-phones"
                label="Mobile number"
                checked={this.isAutoLinkFieldChecked('phones.mobile')}
                size="small"
                value="phones.mobile"
                onChange={this.handleChangeAutoLinkFields.bind(this, 'phones.mobile')}
              />
            </div>
            <div className="lookupApiConfigForm-subtext">If more than one is available, all will be used in search</div>
          </div>

          {this.renderAutoLinkCheckboxes()}
        </div>

        <div className="lookupApiConfigForm-autoLink-centered-text">
          <p>
            If you have any questions, try taking a look at{' '}
            <a href="https://developer.gladly.com/rest/#tag/Customer-Lookup" target="_blank" rel="noreferrer">
              Customer Lookup Guide
            </a>
          </p>
        </div>
      </div>
    );
  }

  renderAutoLinkCheckboxes() {
    return _.map(this.props.profileCustomAttributes, (customerAttribute, index) => {
      var customAttributeAttr = 'customattribute.' + customerAttribute.attr;
      return (
        <div key={index} className="lookupApiConfigForm-field">
          <Checkbox
            label={customerAttribute.label}
            checked={this.isAutoLinkFieldChecked(customAttributeAttr)}
            size="small"
            value={customAttributeAttr}
            onChange={this.handleChangeAutoLinkFields.bind(this, customAttributeAttr)}
          />
        </div>
      );
    });
  }

  isAutoLinkFieldChecked(attr) {
    return this.state.autoLinkFields.indexOf(attr) !== -1;
  }

  handleChangeAutoLinkFields(attr, e) {
    var newAutoLinkFields;

    if (e.target.checked) {
      newAutoLinkFields = this.state.autoLinkFields.concat(attr);
    } else {
      newAutoLinkFields = this.state.autoLinkFields.filter(v => v !== attr);
    }

    this.setState({ autoLinkFields: newAutoLinkFields });
  }

  handleRequestSignatureKeyChange(newKeyValue) {
    this.setState({ signingKey: newKeyValue.target.value });
  }

  renderButtons() {
    if (this.props.hasConnector) {
      return null;
    }

    return (
      <div className="lookupApiConfigForm-controls">
        <div className="lookupApiConfigForm-controls-wrapper">
          <Button
            className="lookupApiConfigForm-cancel"
            onClick={this.handleClickCancel}
            buttonType={ButtonTypes.TEXT}
            disabled={this.props.isPending}
            blurOnClick
          >
            Cancel
          </Button>

          <Button
            className="lookupApiConfigForm-save"
            onClick={this.handleClickSave}
            buttonType={ButtonTypes.PRIMARY}
            disabled={this.props.isPending}
            blurOnClick
          >
            Save
          </Button>
        </div>
      </div>
    );
  }
}

LookupApiConfigForm.propTypes = {
  errors: PropTypes.arrayOf(PropTypes.instanceOf(Err)),
  hasConnector: PropTypes.bool,
  initialCustomerLookupConfig: PropTypes.instanceOf(CustomerLookupConfig),
  isPending: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  profileCustomAttributes: PropTypes.arrayOf(PropTypes.instanceOf(AttributeDef)),
};

export default connect(mapStateToProps, mapExecuteToProps)(LookupApiConfigForm);



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/admin/integrations/lookup_api_config/lookup_api_config_form.jsx